/**
 * Single source of truth for the top-level navigation tabs.
 *
 * Each entry declares:
 *   - which feature flag(s) control its visibility (flag + optional section)
 *   - which audiences can see it (empty = everyone)
 *   - the icon (lucide name) and an optional badge label
 *   - a stable `position` for deterministic ordering
 *
 * The actual list of visible tabs is computed by `resolveNavTabs()` against a
 * snapshot + audience set, so the *same* rule runs on the server (initial
 * paint) and on the client (after a route change) — no flicker, no
 * hydration mismatches.
 */
import type { LucideIcon } from "lucide-react";
import {
  Home,
  ArrowLeftRight,
  Flame,
  LayoutGrid,
  DollarSign,
  Tag,
  Plane,
  Sparkles,
} from "lucide-react";
import { ROUTES } from "@/lib/routes";

export interface NavTabDefinition {
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
  /** Lower comes first. Gaps are intentional so admins can splice tabs in. */
  position: number;
  /** Section gate (must be enabled). */
  section?: string;
  /** Sub-feature gate (must be enabled, in addition to its section). */
  flag?: string;
  /** Audience tokens; ANY match grants access. Empty = no restriction. */
  audiences?: readonly string[];
  /** Optional pill rendered next to the label. */
  badge?: string;
  /** Show on mobile-only (`mobile`) or desktop-only (`desktop`). */
  visibility?: "all" | "mobile" | "desktop";
}

/**
 * Source list. Order in this array is irrelevant — `position` drives display.
 * To add a tab: append an entry with a unique id, a flag key under the
 * `navigation` section in the flag registry, and a position number.
 */
export const NAV_TABS: readonly NavTabDefinition[] = [
  {
    id: "home",
    label: "Inicio",
    href: ROUTES.home,
    icon: Home,
    position: 10,
    flag: "navigation.tabHome",
  },
  {
    id: "comparador",
    label: "Comparador",
    href: ROUTES.comparador.root,
    icon: ArrowLeftRight,
    position: 20,
    section: "products",
    flag: "navigation.tabComparador",
  },
  {
    id: "ofertas",
    label: "Ofertas",
    href: ROUTES.comparador.ofertas,
    icon: Flame,
    position: 30,
    section: "products",
    flag: "navigation.tabOfertas",
  },
  {
    id: "categorias",
    label: "Categorías",
    href: ROUTES.categorias,
    icon: LayoutGrid,
    position: 40,
    section: "categories",
    flag: "navigation.tabCategorias",
  },
  {
    id: "cashback",
    label: "Cashback",
    href: ROUTES.cashback,
    icon: DollarSign,
    position: 50,
    section: "cashback",
    flag: "navigation.tabCashback",
    badge: "Nuevo",
  },
  {
    id: "cupones",
    label: "Cupones",
    href: ROUTES.cupones,
    icon: Tag,
    position: 60,
    section: "coupons",
    flag: "navigation.tabCupones",
  },
  {
    id: "viajes",
    label: "Viajes",
    href: ROUTES.viajes,
    icon: Plane,
    position: 70,
    section: "viajes",
    flag: "navigation.tabViajes",
    badge: "Nuevo",
  },
  {
    id: "rufus",
    label: "Rufus",
    href: ROUTES.rufus,
    icon: Sparkles,
    position: 80,
    section: "rufus",
    flag: "navigation.tabRufus",
    badge: "IA",
  },
];

export interface NavResolutionInput {
  /** Snapshot from the flags provider / server. */
  flags: Record<string, boolean>;
  /** Audience tokens active for the current request. */
  audiences: readonly string[];
}

/**
 * Pure resolver. Same shape as the evaluator's audience match: empty
 * `audiences` on a tab means "no restriction", otherwise we OR-match.
 *
 * Sub-features inherit their section's state — if `section` is off, the tab
 * is hidden regardless of its own flag value.
 */
export function resolveNavTabs(
  input: NavResolutionInput
): readonly NavTabDefinition[] {
  const { flags, audiences } = input;
  const audSet = new Set(audiences);
  const out: NavTabDefinition[] = [];

  for (const tab of NAV_TABS) {
    if (tab.section && flags[tab.section] === false) continue;
    if (tab.flag && flags[tab.flag] === false) continue;
    if (tab.audiences && tab.audiences.length > 0) {
      const ok = tab.audiences.some((t) => audSet.has(t) || t === "all");
      if (!ok) continue;
    }
    out.push(tab);
  }
  return out.slice().sort((a, b) => a.position - b.position);
}
